import React, { useCallback, useEffect, useRef, useState } from 'react';

import { VisuallyHidden } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { dollarsToCents } from 'utils';

import Currency from 'components/currency';
import { FloatingLabelInput } from 'components/floating-label-fields';
import { useLocale } from 'state/intl';
import { CURRENCY_PREFIX } from 'state/intl/types';
import { TipAmounts, useOrderContext } from 'state/order';
import { ITipSelection } from 'state/order/types';
import { FormValidationState } from 'utils/form';
import { checkIfValidTip } from 'utils/payment';

import theme from '../theme';

const Fieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 1rem 0 0 0;
`;

const StyledFloatingLabelInput = styled(FloatingLabelInput)`
  & {
    margin-bottom: 0.5rem;
  }
`;

const Container = styled.div`
  display: flex;
`;

const Label = styled.label`
  display: block;
  border: 1px solid ${Styles.color.grey.three};
  color: ${theme.tipSelectorLabelColor};
  font-size: 0.9375rem;
  padding: 0.5rem 0;
  text-align: center;
  cursor: pointer;

  :hover {
    background: ${theme.tipSelectionBackground};
    color: ${Styles.color.white};
    border-color: ${theme.tipSelectionBackground};
    z-index: ${Styles.zIndex.below};
  }
`;

const Group = styled.div`
  flex: 1 0 auto;

  :first-of-type label {
    border-radius: ${Styles.borderRadius} 0 0 ${Styles.borderRadius};
  }

  :last-child label {
    border-radius: 0 ${Styles.borderRadius} ${Styles.borderRadius} 0;
  }
`;

const Radio = styled.input`
  opacity: 0;
  font-family: ${Styles.fontFamily.base};

  :checked + label {
    background: ${theme.tipSelectionBackground};
    color: ${Styles.color.white};
    border-color: ${theme.tipSelectionBackground};
    z-index: ${Styles.zIndex.below};
  }

  :focus + label {
    outline: -webkit-focus-ring-color auto 5px;
    outline-color: -webkit-focus-ring-color;
    outline-style: auto;
    outline-width: 5px;
  }

  /* work around for Edge not supporting outline-style: auto */
  @supports (-ms-ime-align: auto) {
    :focus + label {
      outline-color: invert;
      outline-width: 3px;
      outline-style: dotted;
    }
  }
`;

const TipErrorMsg = styled.div`
  margin-top: 2px;
  color: ${Styles.color.error};
  font-size: 0.7rem;
`;

const MAX_TIP_LENGTH = '6'; // Dont allow number longer than $99.99

const currencyMask = (locale: string) =>
  createNumberMask({
    prefix: CURRENCY_PREFIX[locale],
    allowDecimal: true,
    decimalLimit: 2,
    allowNegative: false,
    allowLeadingZeroes: false,
    includeThousandsSeparator: false,
  });

type Props = {
  subTotalCents: number;
  showTipPercentage: boolean;
  otherDiscountAmount: number;
  isLoading?: boolean;
  isDisabled?: boolean;
};

const DEFAULT_TIP_AMOUNT = '0';

const TipSelector: React.FC<Props> = ({
  subTotalCents,
  otherDiscountAmount,
  showTipPercentage,
  isLoading,
  isDisabled,
}) => {
  const { formatMessage } = useIntl();
  const { locale } = useLocale();
  const {
    setTipSelection,
    tipSelection,
    updateTipAmount,
    tipAmount: currentTipAmount,
  } = useOrderContext();

  const onTipPercentChange = useCallback(
    (tipPercent: string) => {
      updateTipAmount({
        subTotalCents,
        otherDiscountAmount,
        newTipOption: 'percent',
        newTipAmount: parseFloat(tipPercent),
      });
    },
    [otherDiscountAmount, subTotalCents, updateTipAmount]
  );

  const onTipAmountChange = useCallback(
    (newTipAmount: string) => {
      updateTipAmount({
        subTotalCents,
        otherDiscountAmount,
        newTipOption: 'dollar',
        newTipAmount: parseFloat(newTipAmount),
      });
    },
    [otherDiscountAmount, subTotalCents, updateTipAmount]
  );

  const tip = showTipPercentage ? tipSelection.percentAmount : tipSelection.dollarAmount;
  const [tipString, setTipString] = useState(
    `${CURRENCY_PREFIX[locale]}${tipSelection.otherAmount}`
  );
  const [errorString, setErrorString] = useState('');
  const hasError = !!errorString;

  const updateOtherTipAmount = (otherAmount: string) => {
    setTipSelection((selection: ITipSelection) => ({
      ...selection,
      isOtherSelected: true,
      otherAmount: Number(otherAmount),
    }));
    onTipAmountChange(otherAmount);
  };

  const handleTipAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Example: '$1.2'

    const tipAmountWithPrefix = e.target.value;
    setTipString(tipAmountWithPrefix);

    if (!tipAmountWithPrefix) {
      setErrorString(formatMessage({ id: 'tipAmountError' }));
      updateOtherTipAmount(DEFAULT_TIP_AMOUNT);
      return;
    }

    // Example: '1.2'
    const tipAmountAsString = tipAmountWithPrefix.slice(CURRENCY_PREFIX[locale].length);

    const isValidTip = checkIfValidTip({
      tipAmount: dollarsToCents(Number(tipAmountAsString)),
      subTotalCents,
    });
    if (!isValidTip) {
      setErrorString(formatMessage({ id: 'tipAmountMaxError' }));
      updateOtherTipAmount(DEFAULT_TIP_AMOUNT);
      return;
    }

    setErrorString('');
    updateOtherTipAmount(tipAmountAsString);
  };

  const onTipChange = useCallback(
    (amount: string) => {
      if (showTipPercentage) {
        onTipPercentChange(amount);
      } else {
        onTipAmountChange(amount);
      }
    },
    [showTipPercentage, onTipAmountChange, onTipPercentChange]
  );
  const buildRadioProps = (tipAmount: string | number) => {
    const numAmount = tipAmount === 'other' ? 0 : tipAmount;
    return {
      name: 'tip',
      id: `tip.${tipAmount}`,
      type: 'radio',
      value: tipAmount,
      checked: tipSelection.isOtherSelected ? tipAmount === 'other' : tip === tipAmount,
      onChange: () => {
        if (isDisabled) {
          return;
        }
        setTipSelection((selection: ITipSelection) => {
          if (tipAmount === 'other') {
            // Reset to default when changing to "Other"
            onTipAmountChange(DEFAULT_TIP_AMOUNT);
            return {
              ...selection,
              isOtherSelected: true,
              otherAmount: 0,
            };
          }
          if (showTipPercentage) {
            return {
              ...selection,
              isOtherSelected: false,
              percentAmount: tipAmount as number,
            };
          }
          return {
            ...selection,
            isOtherSelected: false,
            dollarAmount: tipAmount as number,
          };
        });
        setTipString('');
        setErrorString('');
        if (tipAmount !== 'other') {
          onTipChange(numAmount.toString());
        }
      },
    };
  };

  // Show the error message above the Tip select btns when the tip is > than the subtotal
  // If the user has a invalid tip, keep the error message when the user clicks on the 'other' button until the user start to type in the input
  const showTipSelectBtnError =
    !isLoading && (!tipSelection.isOtherSelected || !tipString) && currentTipAmount > subTotalCents;

  const tipOtherInputRef = useRef<HTMLInputElement>(null);
  const { isOtherSelected } = tipSelection;
  const previousTipOtherSelected = useRef(isOtherSelected);

  // focus tip input only when "Other" option is first selected
  // prevents keyboard from opening on mobile devices when cart renders Other by default
  useEffect(() => {
    if (!previousTipOtherSelected.current && isOtherSelected && tipOtherInputRef.current) {
      tipOtherInputRef.current.focus();
    }
  }, [isOtherSelected]);

  return (
    <Fieldset>
      <Container>
        <VisuallyHidden as="legend">Select Tip</VisuallyHidden>
        {showTipPercentage
          ? TipAmounts.PERCENT_AMOUNTS.map(amount => (
              <Group key={`tip.${amount}`}>
                <Radio
                  aria-labelledby={`tip${amount}`}
                  {...buildRadioProps(amount)}
                  disabled={isDisabled}
                />
                <Label id={`tip${amount}`} htmlFor={`tip.${amount}`}>
                  {`${amount}%`}
                </Label>
              </Group>
            ))
          : TipAmounts.DOLLAR_AMOUNTS.map(amount => (
              <Group key={`tip.${amount}`}>
                <Radio
                  aria-labelledby={`tip${amount}`}
                  {...buildRadioProps(amount)}
                  disabled={isDisabled}
                />
                <Label id={`tip${amount}`} htmlFor={`tip.${amount}`}>
                  <Currency amount={amount * 100} isLoading={isLoading} />
                </Label>
              </Group>
            ))}
        <Group key="tip.other">
          <Radio aria-labelledby="tipOther" {...buildRadioProps('other')} />
          <Label id="tipOther" htmlFor="tip.other">
            {formatMessage({ id: 'other' })}
          </Label>
        </Group>
      </Container>

      {showTipSelectBtnError && (
        <TipErrorMsg role="alert" data-testid="showTipSelectBtnError">
          {formatMessage({ id: 'tipAmountMaxError' })}
        </TipErrorMsg>
      )}

      {isOtherSelected && (
        <StyledFloatingLabelInput
          css={`
            margin: 1.5rem 0 0 0;
          `}
          type="text"
          label={formatMessage({ id: 'tipField' })}
          name="customTip"
          value={tipString}
          validation={hasError && FormValidationState.INVALID}
          message={hasError && errorString}
          onChange={handleTipAmountChange}
          mask={currencyMask(locale)}
          maxLength={MAX_TIP_LENGTH}
          data-testid="customTipInput"
          ref={tipOtherInputRef}
          disabled={isDisabled}
        />
      )}
    </Fieldset>
  );
};

export default TipSelector;
